import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store';
import { api } from '@/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Wallet, DollarSign, Users, Clock, CheckCircle2, XCircle, Settings, Loader2, Search, ArrowDownLeft, TrendingUp, Gift } from 'lucide-react';

export default function NexuPayAdminPage() {
  const [tab, setTab] = useState('dashboard');
  const [refCfg, setRefCfg] = useState({ enabled: false, commissions: { simple: '', reseller: '', master: '' } });
  const [refSaving, setRefSaving] = useState(false);
  const [dash, setDash] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wStatus, setWStatus] = useState('pending');
  const [wPage, setWPage] = useState(1);
  const [wTotal, setWTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [uPage, setUPage] = useState(1);
  const [wallets, setWallets] = useState([]);
  const [walletSort, setWalletSort] = useState('saldo_desc'); // saldo_desc | saldo_asc | nome
  const [wlPage, setWlPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const [adjustOpen, setAdjustOpen] = useState(null); // { user_id, username, balance }
  const [adjustBalance, setAdjustBalance] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // Bonus state
  const [bonusConfig, setBonusConfig] = useState({ active: false, min_transactions: 50, description: '' });
  const [bonusProgress, setBonusProgress] = useState([]);
  const [savingBonus, setSavingBonus] = useState(false);

  const handleAdjust = async () => {
    if (adjustBalance === '' || adjustBalance === null) return;
    setAdjusting(true);
    try {
      const r = await api.post(`/nexupay/admin/wallets/${adjustOpen.user_id}/adjust`, {
        new_balance: parseFloat(adjustBalance),
        reason: adjustReason || 'Ajuste manual'
      });
      toast.success(r.data.message);
      setAdjustOpen(null);
      loadWallets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setAdjusting(false); }
  };

  const PER_PAGE = 10;

  const loadDash = async () => {
    try { const r = await api.get('/nexupay/admin/dashboard'); setDash(r.data); setSettingsForm(r.data.settings || {}); } catch {} finally { setLoading(false); }
  };
  const loadWithdrawals = async (status = 'pending', page = 1) => {
    try { const r = await api.get(`/nexupay/admin/withdrawals?status=${status}&page=${page}&limit=${PER_PAGE}`); setWithdrawals(r.data.withdrawals); setWTotal(r.data.total); setWPage(page); } catch {}
  };
  const loadUsers = async () => { try { const r = await api.get('/nexupay/admin/users'); setUsers(r.data); } catch {} };
  const loadWallets = async () => { try { const r = await api.get(`/nexupay/admin/wallets?search=${search}`); setWallets(r.data); } catch {} };
  const loadBonus = async () => {
    try {
      const r = await api.get('/nexupay/admin/bonus-config');
      setBonusConfig({ active: r.data.active, min_transactions: r.data.min_transactions, description: r.data.description || '' });
      setBonusProgress(r.data.reseller_progress || []);
    } catch {}
  };
  const handleSaveBonus = async () => {
    setSavingBonus(true);
    try { await api.put('/nexupay/admin/bonus-config', bonusConfig); toast.success('Configuração salva!'); loadBonus(); } catch { toast.error('Erro'); }
    finally { setSavingBonus(false); }
  };
  const handleGrantBonus = async (userId) => {
    if (!confirm('Conceder 1 mês grátis para este revendedor?')) return;
    try { const r = await api.post(`/nexupay/admin/bonus-grant/${userId}`); toast.success(r.data.message); loadBonus(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  useEffect(() => { loadDash(); loadWithdrawals(); loadUsers(); }, []);
  useEffect(() => { if (tab === 'wallets') loadWallets(); }, [tab, search]);
  useEffect(() => { if (tab === 'bonus') loadBonus(); }, [tab]);
  useEffect(() => {
    if (tab === 'referral') {
      api.get('/admin/reseller-referral').then((r) => {
        const c = r.data.commissions || {};
        setRefCfg({ enabled: !!r.data.enabled, commissions: { simple: c.simple ?? '', reseller: c.reseller ?? '', master: c.master ?? '' } });
      }).catch(() => {});
    }
  }, [tab]);
  const saveRefCfg = async () => {
    setRefSaving(true);
    try {
      await api.put('/admin/reseller-referral', {
        enabled: refCfg.enabled,
        commissions: {
          simple: parseFloat(refCfg.commissions.simple) || 0,
          reseller: parseFloat(refCfg.commissions.reseller) || 0,
          master: parseFloat(refCfg.commissions.master) || 0,
        },
      });
      toast.success('Configuração de indicação salva!');
    } catch (e) { toast.error('Erro ao salvar'); }
    finally { setRefSaving(false); }
  };

  const handleApprove = async (id) => {
    if (!confirm('Aprovar e pagar via AtivoPay automaticamente?')) return;
    try {
      const r = await api.post(`/nexupay/admin/withdrawals/${id}/approve`);
      toast.success(r.data.message || 'Aprovado!');
      loadWithdrawals(wStatus); loadDash();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const handleApproveManual = async (id) => {
    if (!confirm('Aprovar manualmente? (voce ja fez o PIX por fora)')) return;
    try {
      const r = await api.post(`/nexupay/admin/withdrawals/${id}/approve-manual`);
      toast.success(r.data.message || 'Aprovado manualmente!');
      loadWithdrawals(wStatus); loadDash();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const handleReject = async () => {
    if (!rejectReason.trim()) return toast.error('Motivo obrigatorio');
    try { await api.post(`/nexupay/admin/withdrawals/${rejectOpen}/reject`, { reason: rejectReason }); toast.success('Recusado'); setRejectOpen(null); setRejectReason(''); loadWithdrawals(wStatus); loadDash(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const handleToggle = async (userId, enabled) => {
    try { await api.post(`/nexupay/admin/toggle/${userId}`, { enabled }); toast.success(enabled ? 'Habilitado' : 'Desabilitado'); loadUsers(); loadDash(); } catch { toast.error('Erro'); }
  };
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try { await api.put('/nexupay/admin/settings', settingsForm); toast.success('Salvo!'); loadDash(); } catch { toast.error('Erro'); }
    finally { setSavingSettings(false); }
  };

  // Paginação helper
  const paginate = (arr, page) => arr.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = (arr) => Math.ceil(arr.length / PER_PAGE);
  const PaginationBar = ({ page, total, onPage }) => total > PER_PAGE ? (
    <div className="flex justify-center gap-2 pt-3">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>Anterior</Button>
      <span className="text-xs text-muted-foreground py-2">Pag {page} de {Math.ceil(total / PER_PAGE)}</span>
      <Button variant="outline" size="sm" disabled={page * PER_PAGE >= total} onClick={() => onPage(page + 1)}>Proxima</Button>
    </div>
  ) : null;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: DollarSign },
    { id: 'withdrawals', label: `Saques${dash?.pending_withdrawals ? ` (${dash.pending_withdrawals})` : ''}`, icon: Clock },
    { id: 'users', label: 'Usuarios', icon: Users },
    { id: 'wallets', label: 'Carteiras', icon: Wallet },
    { id: 'bonus', label: 'Bônus', icon: TrendingUp },
    { id: 'referral', label: 'Indicação', icon: Gift },
    { id: 'settings', label: 'Config', icon: Settings },
  ];

  return (
    <div className="space-y-6" data-testid="nexupay-admin">
      <div>
        <h1 className="text-2xl font-bold">NexuPay — Admin</h1>
        <p className="text-sm text-muted-foreground">Gerenciamento da carteira digital</p>
      </div>

      <div className="flex gap-1 p-1 rounded-lg bg-muted/30 w-fit overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'withdrawals') loadWithdrawals(wStatus); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {tab === 'dashboard' && dash && (
        <div className="space-y-3">
          {dash.ativopay_enabled ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <p className="text-xs text-emerald-500">PIX-Out automatico via AtivoPay ativo — saques aprovados serao pagos automaticamente</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 border border-border/50">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">PIX-Out manual — ative AtivoPay nos Gateways para saques automaticos</p>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Total em Carteiras</p><p className="text-xl font-black text-emerald-500">R$ {dash.total_balance?.toFixed(2)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Disponivel</p><p className="text-xl font-black text-foreground">R$ {dash.total_available?.toFixed(2)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Taxas Arrecadadas</p><p className="text-xl font-black text-yellow-500">R$ {dash.total_fees?.toFixed(2)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Total Sacado</p><p className="text-xl font-black text-red-500">R$ {dash.total_withdrawn?.toFixed(2)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Saques Pendentes</p><p className="text-xl font-black text-yellow-500">{dash.pending_withdrawals}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Carteiras Ativas</p><p className="text-xl font-black text-foreground">{dash.active_wallets}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Total Recebido</p><p className="text-xl font-black text-emerald-500">R$ {dash.total_received?.toFixed(2)}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-muted-foreground">Bloqueado</p><p className="text-xl font-black text-orange-500">R$ {dash.total_blocked?.toFixed(2)}</p></CardContent></Card>
          </div>
        </div>
      )}

      {/* WITHDRAWALS */}
      {tab === 'withdrawals' && (
        <div className="space-y-4">
          <div className="flex gap-1">
            {['pending', 'approved', 'rejected'].map(s => (
              <button key={s} onClick={() => { setWStatus(s); setWPage(1); loadWithdrawals(s, 1); }}
                className={`px-3 py-1.5 text-xs rounded-md ${wStatus === s ? 'bg-primary text-primary-foreground' : 'bg-muted/30 text-muted-foreground'}`}>
                {s === 'pending' ? 'Pendentes' : s === 'approved' ? 'Aprovados' : 'Recusados'}
              </button>
            ))}
          </div>
          {withdrawals.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Nenhum saque</p> : (
            <div className="space-y-2">
              {withdrawals.map(w => (
                <Card key={w.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-bold">{w.username}</p>
                          <Badge variant={w.status === 'pending' ? 'secondary' : w.status === 'approved' ? 'default' : 'destructive'} className="text-[10px]">
                            {w.status === 'pending' ? 'Pendente' : w.status === 'approved' ? 'Aprovado' : 'Recusado'}
                          </Badge>
                        </div>
                        <p className="text-lg font-black text-emerald-500">R$ {w.amount?.toFixed(2)}</p>
                        <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground mt-1">
                          <span>PIX: <code className="text-foreground">{w.pix_key}</code> ({w.pix_key_type})</span>
                          {w.beneficiary_name && <span>Benef: {w.beneficiary_name}</span>}
                          {w.beneficiary_document && <span>Doc: {w.beneficiary_document}</span>}
                          <span>Taxa: R$ {w.fee?.toFixed(2)}</span>
                          <span>{new Date(w.created_at).toLocaleString('pt-BR')}</span>
                        </div>
                        {w.reject_reason && <p className="text-xs text-red-400 mt-1">Motivo: {w.reject_reason}</p>}
                        {w.pix_out_method === 'manual' && w.status === 'approved' && <p className="text-[10px] text-amber-400 mt-1">Aprovado manualmente</p>}
                        {w.pix_out_method === 'ativopay' && w.status === 'approved' && <p className="text-[10px] text-emerald-400 mt-1">Pago via AtivoPay</p>}
                      </div>
                      {w.status === 'pending' && (
                        <div className="flex flex-col gap-1 shrink-0">
                          {dash?.ativopay_enabled && (
                            <Button size="sm" onClick={() => handleApprove(w.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Pagar
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleApproveManual(w.id)} className="h-8 border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Aprovar Manual
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => { setRejectOpen(w.id); setRejectReason(''); }} className="h-8"><XCircle className="w-3.5 h-3.5 mr-1" />Recusar</Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              <PaginationBar page={wPage} total={wTotal} onPage={(p) => loadWithdrawals(wStatus, p)} />
            </div>
          )}
        </div>
      )}

      {/* USERS */}
      {tab === 'users' && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Habilitar/desabilitar NexuPay por usuario ({users.length})</p>
          {paginate(users, uPage).map(u => (
            <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
              <div>
                <p className="text-sm font-medium">{u.username} <Badge variant="outline" className="text-[10px] ml-1">{u.role}</Badge></p>
                {u.nexupay_enabled && <p className="text-[10px] text-muted-foreground">Saldo: R$ {u.balance?.toFixed(2)}</p>}
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={u.nexupay_enabled} onChange={e => handleToggle(u.id, e.target.checked)} className="sr-only peer" />
                <div className="w-10 h-5 bg-muted rounded-full peer peer-checked:bg-emerald-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
              </label>
            </div>
          ))}
          <PaginationBar page={uPage} total={users.length} onPage={setUPage} />
        </div>
      )}

      {/* WALLETS */}
      {tab === 'wallets' && (
        <div className="space-y-3">
          <div className="relative"><Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => { setSearch(e.target.value); setWlPage(1); }} placeholder="Buscar por nome..." className="pl-9 h-9" /></div>
          <div className="flex items-center gap-1.5">
            {[['saldo_desc','💰 Maior saldo'],['saldo_asc','Menor saldo'],['nome','A-Z']].map(([k,label]) => (
              <button key={k} onClick={() => { setWalletSort(k); setWlPage(1); }}
                className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${walletSort === k ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-500' : 'bg-muted/30 border-transparent text-muted-foreground hover:border-border'}`}>
                {label}
              </button>
            ))}
          </div>
          {paginate([...wallets].sort((a,b) => {
            if (walletSort === 'saldo_desc') return (b.balance||0) - (a.balance||0);
            if (walletSort === 'saldo_asc') return (a.balance||0) - (b.balance||0);
            return (a.username||'').localeCompare(b.username||'');
          }), wlPage).map(w => (
            <div key={w.wallet_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
              <div>
                <p className="text-sm font-medium">{w.username} <Badge variant="outline" className="text-[10px] ml-1">{w.role}</Badge></p>
                <p className="text-[10px] text-muted-foreground">Recebido: R$ {w.total_received?.toFixed(2)} · Sacado: R$ {w.total_withdrawn?.toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="text-sm font-bold text-emerald-500">R$ {w.balance?.toFixed(2)}</p>
                  {w.blocked_balance > 0 && <p className="text-[10px] text-yellow-500">Bloqueado: R$ {w.blocked_balance?.toFixed(2)}</p>}
                </div>
                <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" onClick={() => { setAdjustOpen(w); setAdjustBalance(w.balance?.toFixed(2) || '0'); setAdjustReason(''); }}>
                  Editar
                </Button>
              </div>
            </div>
          ))}
          <PaginationBar page={wlPage} total={wallets.length} onPage={setWlPage} />
        </div>
      )}

      {/* SETTINGS */}
      {tab === 'settings' && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm">Configuracoes de Taxas</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Taxa por Recebimento (R$)</Label><Input type="number" step="0.01" min="0" value={settingsForm.receive_fee ?? ''} onChange={e => setSettingsForm({...settingsForm, receive_fee: parseFloat(e.target.value)})} className="h-9 mt-1" /></div>
              <div><Label className="text-xs">Taxa por Saque (R$)</Label><Input type="number" step="0.01" min="0" value={settingsForm.withdraw_fee ?? ''} onChange={e => setSettingsForm({...settingsForm, withdraw_fee: parseFloat(e.target.value)})} className="h-9 mt-1" /></div>
              <div><Label className="text-xs">Saque Minimo (R$)</Label><Input type="number" step="0.01" min="1" value={settingsForm.min_withdraw ?? ''} onChange={e => setSettingsForm({...settingsForm, min_withdraw: parseFloat(e.target.value)})} className="h-9 mt-1" /></div>
              <div><Label className="text-xs">Saque Maximo (R$)</Label><Input type="number" step="0.01" min="0" value={settingsForm.max_withdraw ?? ''} onChange={e => setSettingsForm({...settingsForm, max_withdraw: parseFloat(e.target.value)})} className="h-9 mt-1" /><p className="text-[10px] text-muted-foreground mt-0.5">0 = sem limite</p></div>
            </div>
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full">{savingSettings ? 'Salvando...' : 'Salvar Configuracoes'}</Button>
          </CardContent>
        </Card>
      )}

      {/* BONUS */}
      {tab === 'bonus' && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Bonus NexuPay — Painel Gratis</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Configure uma meta de transacoes NexuPay por mes. Revendedores diretos que atingirem a meta ganham 1 mes de painel gratis automaticamente no dia 1 do mes seguinte.
              </p>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Bonus ativo</Label>
                <button
                  onClick={() => setBonusConfig({...bonusConfig, active: !bonusConfig.active})}
                  className={`w-11 h-6 rounded-full transition-colors ${bonusConfig.active ? 'bg-primary' : 'bg-muted'} relative`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${bonusConfig.active ? 'translate-x-5.5 left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
              <div>
                <Label className="text-xs">Minimo de transacoes no mes</Label>
                <Input type="number" min="1" value={bonusConfig.min_transactions}
                  onChange={e => setBonusConfig({...bonusConfig, min_transactions: parseInt(e.target.value) || 1})}
                  className="h-9 mt-1" data-testid="bonus-min-tx" />
                <p className="text-[10px] text-muted-foreground mt-0.5">Apenas transacoes NexuPay de renovacao de clientes contam</p>
              </div>
              <div>
                <Label className="text-xs">Descricao (opcional)</Label>
                <Input value={bonusConfig.description}
                  onChange={e => setBonusConfig({...bonusConfig, description: e.target.value})}
                  placeholder="Ex: Meta mensal de vendas" className="h-9 mt-1" />
              </div>
              <Button onClick={handleSaveBonus} disabled={savingBonus} className="w-full" data-testid="save-bonus-config">
                {savingBonus ? 'Salvando...' : 'Salvar Configuracao de Bonus'}
              </Button>
            </CardContent>
          </Card>

          {/* Progresso dos revendedores */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Progresso dos Revendedores (Mes Atual)</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">Apenas revendedores diretos do admin. Meta: {bonusConfig.min_transactions} transacoes.</p>
              {bonusProgress.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum revendedor direto encontrado</p>
              ) : (
                <div className="space-y-2">
                  {bonusProgress.map(r => {
                    const pct = Math.min(100, Math.round((r.tx_count / (bonusConfig.min_transactions || 1)) * 100));
                    return (
                      <div key={r.id} className="flex items-center gap-3 p-2 rounded-lg border border-border/50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium truncate">{r.username}</span>
                            <span className="text-xs text-muted-foreground">{r.tx_count}/{bonusConfig.min_transactions}</span>
                          </div>
                          <div className="w-full h-1.5 bg-muted rounded-full mt-1">
                            <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : 'bg-primary'}`} style={{width: `${pct}%`}} />
                          </div>
                        </div>
                        {r.bonus_granted ? (
                          <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20 flex-shrink-0">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Ganhou
                          </Badge>
                        ) : pct >= 100 ? (
                          <Button size="sm" className="h-7 text-xs flex-shrink-0" onClick={() => handleGrantBonus(r.id)} data-testid={`grant-bonus-${r.id}`}>
                            Conceder
                          </Button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={!!rejectOpen} onOpenChange={() => setRejectOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Recusar Saque</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label className="text-xs">Motivo da Recusa *</Label><textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="w-full text-sm p-2 rounded-md border bg-background mt-1 resize-none" placeholder="Informe o motivo..." /></div>
            <Button onClick={handleReject} variant="destructive" className="w-full">Recusar Saque</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Balance Dialog */}
      <Dialog open={!!adjustOpen} onOpenChange={() => setAdjustOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ajustar Saldo</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">Usuario: <span className="text-foreground font-medium">{adjustOpen?.username}</span></p>
            <p className="text-sm text-muted-foreground">Saldo atual: <span className="text-foreground font-bold">R$ {adjustOpen?.balance?.toFixed(2)}</span></p>
            <div>
              <Label className="text-xs">Novo Saldo (R$) *</Label>
              <Input type="number" step="0.01" value={adjustBalance} onChange={e => setAdjustBalance(e.target.value)} className="h-9 mt-1" />
            </div>
            <div>
              <Label className="text-xs">Motivo do ajuste</Label>
              <textarea value={adjustReason} onChange={e => setAdjustReason(e.target.value)} rows={2} className="w-full text-sm p-2 rounded-md border bg-background mt-1 resize-none" placeholder="Ex: Correção de saldo duplicado" />
            </div>
            <Button onClick={handleAdjust} disabled={adjusting} className="w-full">
              {adjusting ? 'Ajustando...' : 'Confirmar Ajuste'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {tab === 'referral' && (
        <Card>
          <CardContent className="p-5 space-y-4 max-w-lg">
            <div>
              <h3 className="font-semibold flex items-center gap-2"><Gift className="w-4 h-4 text-primary" /> Programa de indicação de revendas</h3>
              <p className="text-sm text-muted-foreground mt-1">Quando um revenda indicado renova, o indicador ganha a comissão no NexuPay (só se estiver ativo).</p>
            </div>
            <label className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5 cursor-pointer">
              <span className="text-sm font-medium">Ativar programa de indicação</span>
              <input type="checkbox" checked={refCfg.enabled} onChange={(e) => setRefCfg({ ...refCfg, enabled: e.target.checked })} className="w-4 h-4 accent-primary" />
            </label>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Comissão mensal por tipo de plano do indicado (R$):</p>
              {[['simple','Simple'],['reseller','Revenda'],['master','Master']].map(([k,label]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="text-sm w-24">{label}</span>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={refCfg.commissions[k]}
                    onChange={(e) => setRefCfg({ ...refCfg, commissions: { ...refCfg.commissions, [k]: e.target.value } })}
                    className="flex-1 bg-muted/40 border border-border/60 rounded-lg px-3 py-2 text-sm" />
                  <span className="text-xs text-muted-foreground">/mês</span>
                </div>
              ))}
            </div>
            <button onClick={saveRefCfg} disabled={refSaving}
              className="w-full bg-primary text-primary-foreground rounded-lg py-2.5 font-semibold text-sm hover:opacity-90">
              {refSaving ? 'Salvando...' : 'Salvar configuração'}
            </button>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
